/**
 * <UserRightsBlock> — блок «Права» в карточке человека.
 * Блок 2 · Ф8 «Права доступа» · В-Э (Р-200, вариант Б: «права человека видны в
 * его карточке»).
 *
 * Монтирует вызывающий, и только управляющему правами (`canManageRights`):
 * блок с порога шлёт запрос, на который остальным сервер отвечает 403.
 *
 * ─── ⚠️ Запретов нет (решение владельца 11.10.2026, Ф8 · В-У) ────────────────
 *
 * «Изначальные настройки отнимать не будем ни у кого. Только новые можно
 * отнимать». Отсюда три вида блока:
 *
 *   · РУКОВОДИТЕЛЬ ОТДЕЛА — восемь строк; шести правам «Добавить» / «Убрать»,
 *     двум («вести договоры», «архивировать») — «не добавляется». Пока ничего
 *     не добавлено, значок строки — «Не добавлено» либо «Нет», а не «По
 *     должности»: по должности у него этих прав нет;
 *   · АДМИНИСТРАТОР — объяснение и перечень положенного простым текстом, НИ
 *     ОДНОЙ строки: настраивать нечего. Восемь строк «По должности» без
 *     единой кнопки читались бы как таблица, в которой что-то можно сделать.
 *     Приглашённый администратор — так же;
 *   · НЕ ПОЛУЧАТЕЛЬ (сотрудник, супервайзер, приглашённый руководитель
 *     отдела, архивный) — объяснение, почему права не добавляются.
 *
 * Исключение у двух последних одно: строка, которой штатно быть не должно, —
 * запрет прежних времён (свой либо унаследованный от ГИПа) или недействующая
 * поправка. Сервер по лежащему запрету по-прежнему отказывает (механизм не
 * вырезан), поэтому она показана как есть — «Отнято», «Закрыто из-за ГИПа» —
 * и сбрасывается. Диалога «Отнять право?», подсказки «деньги отняты, а журнал
 * оставлен» и перечня помощников-наследников в блоке больше нет: ставить
 * запрет нечем.
 *
 * ─── Устройство ─────────────────────────────────────────────────────────────
 *
 * У строки — что стоит сейчас и одно действие. ДЕЙСТВИЕ — КНОПКОЙ, А НЕ
 * ПЕРЕКЛЮЧАТЕЛЕМ: состояний у строки больше двух (по должности / добавлено /
 * не действует, плюс два состояния показа лежащего запрета), и
 * двухпозиционный переключатель соврал бы про остальные.
 *
 * «Что даёт должность» сказано ОДИН раз, в шапке: сервер ставит это по уровню,
 * у всех восьми строк значение одинаково, и повторять его построчно значило
 * вдвое удлинить окно. Построчно остаётся только оговорка, где «по должности»
 * — не «везде» (`byRoleOwnObjectsOnly`).
 *
 * Что предложить, решает `describeRightRow` — недопустимое не рисуется вовсе:
 * «Вести договоры» и «архивировать» руководителю не добавляются (Р-217 б;
 * решение 10.10.2026), «вести» без «видеть» не предлагается (Р-195),
 * унаследованный от ГИПа запрет здесь не сбрасывается (Р-197). Сервер на всё
 * это ответит 422 и сам — на случай, если карточка устарела, отказ
 * показывается понятным текстом, а карточка перечитывается.
 *
 * ─── Запись — по одной ──────────────────────────────────────────────────────
 *
 * Пока запрос в пути, кнопки строк помечены `aria-disabled` и щелчок не
 * принимают; синхронный замок (`inFlight`) держит двойной щелчок раньше
 * перерисовки (договор В-О1б: «`PUT` слать по одному»). `aria-disabled`, а не
 * `disabled`: выключенная кнопка теряет фокус, и после записи человек с
 * клавиатуры оказывался в начале окна.
 *
 * ─── Диалог подтверждения — один ────────────────────────────────────────────
 *
 * Спрашивается только «Добавить» общий журнал руководителю отдела: журнал
 * открывает всё и без фильтра (Р-190).
 *
 * ─── Фокус после диалога подтверждения ──────────────────────────────────────
 *
 * Возвращается на кнопку ТОЙ ЖЕ СТРОКИ — после «Отмены», Escape и после
 * подтверждения. Свой возврат, а не унаследованный: диалог открыт пропом
 * `open`, без `AlertDialogTrigger`, и Radix в этом случае фокус не возвращает
 * никуда (разбор — `ui/archive-confirm-dialog.tsx`, `returnFocusRef`). Цель
 * ищется по праву В МИГ ЗАКРЫТИЯ, а не запоминается узлом при открытии: щелчок
 * мышью кнопку не фокусирует в Safari и Firefox на macOS, и «что было в фокусе
 * при открытии» там — не кнопка.
 *
 * ─── Отказы ─────────────────────────────────────────────────────────────────
 *
 *   409 `VERSION_CONFLICT`        — карточку перечитать, сказать «состояние
 *                                   изменилось»; сам запрос НЕ повторяется;
 *   409 `serialization_conflict`  — тихий повтор в клиенте API (ограниченно);
 *                                   сюда доходит только исчерпание;
 *   422 двух видов                — понятный текст и перечитать; у «действие
 *                                   не применимо» текст — сервера (причин под
 *                                   кодом несколько), словарный — запасной;
 *   обрыв связи                   — «не удалось подтвердить»: дошёл ли запрос,
 *                                   неизвестно, поэтому карточка перечитывается;
 *   403, 404, прочее              — текстом.
 *
 * Отказ показывается В СТРОКЕ, которую нажали (`role="alert"`), и прокручивается
 * в видимую область: блок выше экрана, и сообщение наверху нижняя строка не
 * увидела бы. «Перечитано» говорится, только если перечитывание удалось.
 * Всплывающее сообщение фетчера для этой записи выключено (`background`), а
 * отказ, который фетчер всё же показал сам (`alreadyReported`: смена роли,
 * увод на вход), здесь не повторяется.
 */
import * as React from 'react';
import { ROLE_NAMES, RightEffect, UserRight, type UserRightsResponse } from '@aris/shared';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { QueryErrorState } from '@/components/ui/query-error-state';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toast';
import { isVersionConflict, useSetUserRightMutation, useUserRightsQuery } from '@/api/user-rights';
import { rightHintRu, rightInertReasonRu, rightNameRu, rightsRu } from '@/i18n';
import { formatDate } from '@/lib/date';
import { ApiError } from '@/lib/fetcher';
import { cn } from '@/lib/utils';

import {
  describeRightsCard,
  type IneligibleReason,
  type RightRowStatus,
  type RightRowView,
} from './user-rights-model';

/**
 * Значок состояния. ⚠️ «Добавлено» — НЕ общий вариант `active`: зелёный текст
 * на зелёной подложке даёт около 3,5 : 1 при норме 4,5. Здесь — тёмный текст на
 * белом с зелёной рамкой; состояния различаются подписью, а не одним цветом.
 */
export const RIGHT_STATUS_BADGE: Readonly<
  Record<RightRowStatus, { variant: NonNullable<BadgeProps['variant']>; className?: string }>
> = {
  ROLE: { variant: 'archived' },
  GRANTED: { variant: 'outline', className: 'border-[rgb(var(--ok))]' },
  DENIED: { variant: 'danger' },
  INHERITED_DENY: { variant: 'danger' },
  INERT: { variant: 'urgent' },
};

/** Подстановка `{ключ}` в строку словаря. */
export function fill(template: string, values: Readonly<Record<string, string>>): string {
  return template.replace(/\{(\w+)\}/g, (whole, key: string) => values[key] ?? whole);
}

type Tone = 'info' | 'error';

interface Failure {
  /** Перечитать карточку: ответ сервера говорит, что она устарела либо что неизвестно, дошла ли запись. */
  readonly reread: boolean;
  readonly tone: Tone;
  /** Текст; `rereadOk` — удалось ли перечитать (при `reread: false` не читается). */
  readonly message: (rereadOk: boolean) => string;
}

function describeFailure(err: unknown): Failure {
  const t = rightsRu.result;
  const plain = (message: string): Failure => ({
    reread: false,
    tone: 'error',
    message: () => message,
  });
  if (isVersionConflict(err)) {
    return {
      reread: true,
      tone: 'info',
      message: (ok) => (ok ? t.versionConflictReread : t.versionConflictStale),
    };
  }
  if (err instanceof ApiError) {
    if (err.status === 409 && err.code === 'serialization_conflict') return plain(t.busy);
    if (err.status === 422 && err.code === 'RIGHT_TARGET_NOT_ELIGIBLE') {
      return { reread: true, tone: 'error', message: () => t.targetNotEligible };
    }
    if (err.status === 422 && err.code === 'RIGHT_EFFECT_NOT_APPLICABLE') {
      // Текст — СЕРВЕРА: под этим кодом несколько причин («руководителю не
      // добавляется», «сначала добавьте „видеть“», «запрет не ставится»), и
      // словарная фраза «к этой должности не применяется» верна не для всех.
      // Берётся из конверта, а не из `err.message`: тот при пустом ответе несёт
      // общую заглушку фетчера, и запасной текст до неё не дошёл бы.
      const serverText = err.envelope?.error?.message;
      const text =
        typeof serverText === 'string' && serverText.trim().length > 0
          ? serverText
          : t.effectNotApplicable;
      return { reread: true, tone: 'error', message: () => text };
    }
    if (err.status === 404) return plain(t.userNotFound);
    if (err.status === 403) return plain(t.forbidden);
    if (err.status === 0) {
      // Связь оборвалась: запрос мог дойти, а ответ — нет.
      return {
        reread: true,
        tone: 'error',
        message: (ok) => (ok ? t.networkReread : t.networkStale),
      };
    }
    if (err.message.length > 0) return plain(err.message);
  }
  return plain(t.unknown);
}

interface Notice {
  /** Строка, к которой относится сообщение. */
  readonly right: UserRight;
  readonly message: string;
  readonly tone: Tone;
}

export interface UserRightsBlockProps {
  userId: string;
}

export function UserRightsBlock({ userId }: UserRightsBlockProps): React.ReactElement {
  const query = useUserRightsQuery(userId);
  const mutation = useSetUserRightMutation();
  const titleId = React.useId();

  const card: UserRightsResponse | undefined = query.data;
  const view = React.useMemo(() => (card ? describeRightsCard(card) : null), [card]);

  const [notice, setNotice] = React.useState<Notice | null>(null);
  // Строка, по которой открыт диалог подтверждения; `null` — диалог закрыт.
  const [pending, setPending] = React.useState<RightRowView | null>(null);
  // Синхронный замок: одна запись за раз, и двойной щелчок — тоже одна.
  const inFlight = React.useRef(false);
  // Кнопки строк по праву и право, чей диалог открыт последним, — чтобы после
  // закрытия диалога вернуть фокус на кнопку той же строки.
  const actionButtons = React.useRef(new Map<UserRight, HTMLButtonElement>());
  const confirmedRight = React.useRef<UserRight | null>(null);
  const registerAction = React.useCallback((right: UserRight, el: HTMLButtonElement | null) => {
    if (el === null) actionButtons.current.delete(right);
    else actionButtons.current.set(right, el);
  }, []);
  const busy = mutation.isPending;

  // Другой человек в той же смонтированной карточке — прежнее сообщение не его.
  React.useEffect(() => {
    setNotice(null);
    setPending(null);
  }, [userId]);

  const { refetch } = query;
  const { mutateAsync } = mutation;
  const write = React.useCallback(
    async (row: RightRowView): Promise<void> => {
      if (row.action === null || inFlight.current) return;
      inFlight.current = true;
      setNotice(null);
      try {
        await mutateAsync({
          userId,
          right: row.right,
          effect: row.action.effect,
          version: row.version,
        });
        toast(rightsRu.result.saved, { variant: 'success' });
      } catch (err) {
        // Фетчер уже показал это сам (смена роли, увод на вход) — второго
        // сообщения об одном и том же не надо.
        if (err instanceof ApiError && err.alreadyReported) return;
        const failure = describeFailure(err);
        let rereadOk = false;
        if (failure.reread) {
          const result = await refetch();
          rereadOk = result.isSuccess && !result.isRefetchError;
        }
        setNotice({ right: row.right, message: failure.message(rereadOk), tone: failure.tone });
      } finally {
        inFlight.current = false;
      }
    },
    [mutateAsync, refetch, userId],
  );

  const onAction = React.useCallback(
    (row: RightRowView): void => {
      if (row.action === null || inFlight.current) return;
      if (!row.action.needsConfirm) {
        void write(row);
        return;
      }
      confirmedRight.current = row.right;
      setPending(row);
    },
    [write],
  );

  const onConfirm = React.useCallback(async (): Promise<void> => {
    if (pending === null) return;
    await write(pending);
    setPending(null);
  }, [pending, write]);

  const orphanNotice =
    notice !== null && view !== null && !view.rows.some((row) => row.right === notice.right)
      ? notice
      : null;

  return (
    <section
      aria-labelledby={titleId}
      data-testid="user-rights-block"
      className="space-y-3 border-t border-line pt-4"
    >
      <header>
        <h3 id={titleId} className="text-sm font-semibold text-ink">
          {rightsRu.block.title}
        </h3>
        {/* Вводная — только руководителю отдела: ему одному здесь есть что
            сделать (Ф8 · В-Т). Администратору и не-получателю то же место
            занимает объяснение ниже, начинающееся с «не настраиваются». */}
        {view !== null && view.ineligible === null && !view.fixedByRole ? (
          <p className="mt-0.5 text-xs text-muted" data-testid="user-rights-intro">
            {rightsRu.block.managerIntro}
            {/* Про «Сбросить» — только если такая кнопка в карточке есть. */}
            {view.rows.some((r) => r.action?.kind === 'RESET' || r.action?.kind === 'RESET_OWN')
              ? ` ${rightsRu.block.managerIntroReset}`
              : ''}
          </p>
        ) : null}
      </header>

      {query.isLoading ? (
        <div
          className="space-y-2"
          data-testid="user-rights-loading"
          role="status"
          aria-label={rightsRu.block.loading}
        >
          {/* Восемь заглушек — по числу строк у руководителя отдела. Высоту
              окна они НЕ держат: у администратора и у не-получателя после
              загрузки остаётся абзац текста, и блок сжимается; у руководителя
              строки выше заглушек (пояснения, подпись «Изменил»). */}
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-[58px] w-full" />
          ))}
        </div>
      ) : !card || !view ? (
        // Только когда показывать НЕЧЕГО. Упавшее перечитывание при живой
        // карточке строк не прячет: рядом со строкой стоит сообщение о том,
        // что перечитать не удалось, а без строк его негде показать.
        <QueryErrorState
          testId="user-rights-error"
          headline={rightsRu.block.loadError}
          description={rightsRu.block.loadErrorHint}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <>
          {view.ineligible !== null ? (
            <IneligibleNote
              reason={view.ineligible}
              role={roleNameOf(card.user)}
              stuck={view.rows.length > 0}
            />
          ) : null}

          {/* Администратор: настраивать нечего (решение владельца 11.10.2026).
              Строки ниже появляются, только если что-то лежит с прежних времён. */}
          {view.fixedByRole ? (
            <div className="space-y-2 text-sm text-ink-2" data-testid="user-rights-fixed-by-role">
              <p className="font-medium text-ink" data-testid="user-rights-fixed-by-role-lead">
                {rightsRu.block.fixedByRoleLead}
              </p>
              {/* Что именно положено — простым текстом: значки и кнопки здесь
                  снова читались бы как таблица, в которой что-то можно сделать. */}
              <p data-testid="user-rights-fixed-by-role-text">
                {fill(rightsRu.block.fixedByRole, { role: roleNameOf(card.user) })}
              </p>
              <p data-testid="user-rights-fixed-by-role-where">{rightsRu.block.fixedByRoleWhere}</p>
              {view.rows.length > 0 ? (
                <p data-testid="user-rights-legacy-rows">{rightsRu.block.legacyRows}</p>
              ) : null}
            </div>
          ) : null}

          {/* Сообщение о строке, которой на экране уже нет (сброшенная строка
              не-получателя) — показать его в самой строке негде. */}
          {orphanNotice !== null ? <NoticeBox notice={orphanNotice} /> : null}

          {view.rows.length > 0 ? (
            <ul
              className="space-y-2"
              data-testid="user-rights-rows"
              aria-label={fill(rightsRu.block.listAriaLabel, { name: card.user.fullName })}
            >
              {view.rows.map((row) => (
                <RightRow
                  key={row.right}
                  row={row}
                  busy={busy}
                  byRoleInline={view.byRole === null}
                  notice={notice !== null && notice.right === row.right ? notice : null}
                  onAction={onAction}
                  registerAction={registerAction}
                />
              ))}
            </ul>
          ) : null}

          {/* Сноска — про то, когда изменение увидит человек: уместна там, где
              есть что менять. Администратору без строк менять нечего. */}
          {view.ineligible === null && (!view.fixedByRole || view.rows.length > 0) ? (
            <p className="text-xs text-muted" data-testid="user-rights-footnote">
              {rightsRu.block.footnote}
            </p>
          ) : null}
        </>
      )}

      <AlertDialog
        open={pending !== null}
        onOpenChange={(next) => {
          if (!next && !busy) setPending(null);
        }}
      >
        <AlertDialogContent
          data-testid="user-rights-confirm"
          onCloseAutoFocus={(event) => {
            const right = confirmedRight.current;
            const target = right !== null ? actionButtons.current.get(right) : undefined;
            // Кнопки у строки больше нет (строка ушла вместе с поправкой
            // не-получателя) — поведение Radix не отменяем: вести некуда.
            if (target === undefined || !target.isConnected) return;
            // `preventDefault` — чтобы после нас фокус не трогал никто. Проверено
            // порчей: в jsdom и без него фокус остаётся на кнопке (внутренний
            // обработчик Radix зовёт пустой `triggerRef`), так что тестом эта
            // строка не держится — она страховка, а не предмет.
            event.preventDefault();
            target.focus({ preventScroll: true });
          }}
        >
          <AlertDialogHeader>
            <AlertDialogTitle>{rightsRu.confirm.allowTitle}</AlertDialogTitle>
            {/*
              ⚠️ `asChild` + `<div>`: описание несёт СПИСОК (Р-251, находка 5), а
              `<ul>` внутри `<p>` (так рисуется описание по умолчанию) — неверная
              разметка. `aria-describedby` окна указывает на этот `<div>`, и
              диктор читает перечень целиком.
            */}
            <AlertDialogDescription asChild data-testid="user-rights-confirm-desc">
              <div>
                {pending !== null && card ? (
                  <>
                    <span className="block text-ink">
                      {fill(rightsRu.confirm.subject, {
                        name: card.user.fullName,
                        right: rightNameRu[pending.right],
                      })}
                    </span>
                    <div className="mt-1" data-testid="user-rights-confirm-consequence">
                      <p>{rightsRu.confirm.allowJournal}</p>
                      <ul className="mt-1 list-disc space-y-0.5 pl-5">
                        {rightsRu.confirm.allowJournalItems.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                      <p className="mt-1">{rightsRu.confirm.allowJournalTail}</p>
                    </div>
                  </>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{rightsRu.confirm.cancel}</AlertDialogCancel>
            <AlertDialogAction
              data-testid="user-rights-confirm-btn"
              disabled={busy}
              onClick={(ev) => {
                // Диалог остаётся, пока запись не вернулась: закрыть его раньше
                // значило бы показать прежнюю строку как «уже сделано».
                ev.preventDefault();
                void onConfirm();
              }}
            >
              {rightsRu.confirm.allowSubmit}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}

/**
 * Название должности — как в сводном списке (`rights.page.tsx`): словарное по
 * слагу, запасное — что прислал сервер.
 */
function roleNameOf(user: UserRightsResponse['user']): string {
  return ROLE_NAMES[user.roleSlug] ?? user.roleName;
}

/**
 * Объяснение не-получателю (Ф8 · В-Т): первой строкой — что настраивать нечего,
 * затем почему и куда идти. Строки прежних времён — отдельным абзацем.
 */
function IneligibleNote({
  reason,
  role,
  stuck,
}: {
  reason: IneligibleReason;
  /** Название должности — для причины «Должность «…» — права к ней не добавляются». */
  role: string;
  stuck: boolean;
}): React.ReactElement {
  const t = rightsRu.ineligible;
  const where = t.where[reason];
  return (
    <div className="space-y-2 text-sm text-ink-2" data-testid="user-rights-ineligible">
      <p className="font-medium text-ink" data-testid="user-rights-ineligible-lead">
        {t.lead[reason]}
      </p>
      <p data-testid="user-rights-ineligible-reason">{fill(t.reason[reason], { role })}</p>
      {where !== null ? <p data-testid="user-rights-ineligible-where">{where}</p> : null}
      {stuck ? <p data-testid="user-rights-ineligible-stuck">{t.stuckRows}</p> : null}
    </div>
  );
}

/** Сообщение об итоге записи. Появившись, прокручивается в видимую область. */
function NoticeBox({ notice }: { notice: Notice }): React.ReactElement {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    ref.current?.scrollIntoView({ block: 'nearest' });
  }, [notice]);
  return (
    <div
      ref={ref}
      role={notice.tone === 'error' ? 'alert' : 'status'}
      data-testid="user-rights-notice"
      className={cn(
        'mt-2 rounded-sm border px-3 py-2 text-sm',
        notice.tone === 'error'
          ? 'border-destructive bg-destructive/10 text-destructive'
          : 'border-warn/40 bg-warn-wash text-ink',
      )}
    >
      {notice.message}
    </div>
  );
}

function scopeNote(row: RightRowView): string | null {
  if (row.scope === 'OBJECT') return rightsRu.note.scopeObject;
  if (row.scope === 'CONTRACTS') return rightsRu.note.scopeContracts;
  if (row.scope === 'ORG') return rightsRu.note.scopeOrg;
  return null;
}

function blockedText(row: RightRowView): string | null {
  if (row.blocked === null) return null;
  if (row.blocked.kind === 'NOT_GRANTABLE') return rightsRu.blocked.notGrantable;
  return fill(rightsRu.blocked.needsView, { view: rightNameRu[row.blocked.view] });
}

/**
 * Подпись значка состояния.
 *
 * ⚠️ `ROLE` у руководителя отдела — не «По должности»: по должности у него
 * этих прав НЕТ, и значок читался как «право есть» (Ф8 · В-У, фикс-пак 1).
 * Руководитель узнаётся по самой строке, без второго источника: причина
 * «не добавляется» и область добавленного есть только у него
 * (`actionOf`, `scopeOfRight`).
 */
function statusText(row: RightRowView): string {
  if (row.status === 'ROLE') {
    if (row.blocked?.kind === 'NOT_GRANTABLE') return rightsRu.roleManager.notGrantable;
    if (row.scope !== null) return rightsRu.roleManager.notGranted;
  }
  return rightsRu.status[row.status];
}

/** Пояснения строки — в том порядке, в каком их надо прочитать. */
function notesOf(row: RightRowView): readonly string[] {
  const notes: string[] = [];
  if (row.status === 'INHERITED_DENY' && row.inheritedFrom !== null) {
    notes.push(fill(rightsRu.note.inheritedFrom, { gip: row.inheritedFrom.fullName }));
    if (row.ownEffect !== RightEffect.INHERIT) notes.push(rightsRu.note.ownUnderInherited);
  }
  if (row.status === 'INERT' && row.ownEffect !== RightEffect.INHERIT && !row.ownEffective) {
    notes.push(
      row.ownEffect === RightEffect.DENY
        ? rightsRu.note.inertStandsDeny
        : rightsRu.note.inertStandsAllow,
    );
    if (row.inertReason !== null) notes.push(rightInertReasonRu[row.inertReason]);
  }
  if (row.status === 'INERT' && row.viewMissing !== null && row.ownEffective) {
    notes.push(fill(rightsRu.note.viewMissingGranted, { view: rightNameRu[row.viewMissing] }));
  }
  if (row.status === 'INERT' && row.viewMissing !== null && row.ownEffect === RightEffect.INHERIT) {
    notes.push(fill(rightsRu.note.viewMissingDenied, { view: rightNameRu[row.viewMissing] }));
  }
  const blocked = blockedText(row);
  if (blocked !== null) notes.push(blocked);
  return notes;
}

function changedLine(row: RightRowView): string | null {
  if (row.changedAt === null) return null;
  const when = formatDate(row.changedAt, 'dd.MM.yyyy HH:mm');
  // Строка есть, а эффекта нет — последнее изменение было сбросом.
  const reset = row.ownEffect === RightEffect.INHERIT;
  if (row.changedBy !== null) {
    return fill(reset ? rightsRu.block.resetBy : rightsRu.block.changedBy, {
      who: row.changedBy.fullName,
      when,
    });
  }
  return fill(reset ? rightsRu.block.resetAt : rightsRu.block.changedAt, { when });
}

interface RightRowProps {
  row: RightRowView;
  busy: boolean;
  /** Печатать «по должности» в строке — когда у строк оно разное. */
  byRoleInline: boolean;
  notice: Notice | null;
  onAction: (row: RightRowView) => void;
  /** Сообщить блоку узел кнопки строки (и `null`, когда кнопка снята). */
  registerAction: (right: UserRight, el: HTMLButtonElement | null) => void;
}

function RightRow({
  row,
  busy,
  byRoleInline,
  notice,
  onAction,
  registerAction,
}: RightRowProps): React.ReactElement {
  const right: UserRight = row.right;
  const actionRef = React.useCallback(
    (el: HTMLButtonElement | null) => registerAction(right, el),
    [registerAction, right],
  );
  const name = rightNameRu[right];
  const notes = notesOf(row);
  const scope = scopeNote(row);
  const changed = changedLine(row);
  const badge = RIGHT_STATUS_BADGE[row.status];
  return (
    <li
      data-testid={`user-right-row-${right}`}
      data-status={row.status}
      // Три колонки: текст, значок, место под кнопку. Место под кнопку — одной
      // ширины у всех строк, поэтому значки стоят по одной вертикали и у строк
      // без кнопки.
      className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-3 gap-y-2 rounded-sm border border-line bg-surface px-3 py-2 sm:grid-cols-[minmax(0,1fr)_auto_5.5rem]"
    >
      <div className="col-span-2 min-w-0 sm:col-span-1">
        <p className="text-sm font-medium text-ink">{name}</p>
        <p className="mt-0.5 text-xs text-muted">
          {rightHintRu[right]}
          {scope !== null ? ` ${scope}` : ''}
        </p>
        {byRoleInline ? (
          <p className="mt-0.5 text-xs text-muted" data-testid={`user-right-by-role-${right}`}>
            {row.byRole ? rightsRu.block.byRoleYes : rightsRu.block.byRoleNo}
          </p>
        ) : null}
        {row.byRoleOwnObjectsOnly ? (
          <p className="mt-0.5 text-xs text-muted" data-testid={`user-right-caveat-${right}`}>
            {rightsRu.block.byRoleOwnObjects}
          </p>
        ) : null}
        {notes.map((note) => (
          <p
            key={note}
            className="mt-0.5 text-xs text-ink-2"
            data-testid={`user-right-note-${right}`}
          >
            {note}
          </p>
        ))}
        {changed !== null ? (
          <p className="mt-0.5 text-xs text-muted" data-testid={`user-right-changed-${right}`}>
            {changed}
          </p>
        ) : null}
        {notice !== null ? <NoticeBox notice={notice} /> : null}
      </div>
      <Badge
        variant={badge.variant}
        className={cn('justify-self-start sm:justify-self-end', badge.className)}
        data-testid={`user-right-status-${right}`}
      >
        {statusText(row)}
      </Badge>
      <div className="justify-self-end sm:w-full">
        {row.action !== null ? (
          <Button
            ref={actionRef}
            type="button"
            size="sm"
            variant="outline"
            // `aria-disabled`, а не `disabled`: фокус остаётся на кнопке, и
            // после записи человек с клавиатуры не оказывается в начале окна.
            aria-disabled={busy}
            aria-label={fill(rightsRu.action.ariaLabel[row.action.kind], { right: name })}
            data-testid={`user-right-action-${right}`}
            data-action={row.action.kind}
            className="w-full aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
            onClick={() => {
              if (!busy) onAction(row);
            }}
          >
            {rightsRu.action[row.action.kind]}
          </Button>
        ) : null}
      </div>
    </li>
  );
}
